import { useContext, useEffect, useMemo, useState } from 'react';
import {
  ColumnFiltersState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { TableType } from '@common/ts/enums';
import { useUrlTable } from '@/hooks/useUrlTable';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LangContext } from '@/context/LangProvider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import UserColumns from './UserColumns';
import InviteToOrganizationModal from '../Modals/InviteToOrganizationModal';
import DataTablePagination from '../paginations/DataTablePagination';
import { useGetOrganizationMembers } from '@/reactQuery/get';
import { getPageCount } from '@/utils/getPageCount';
import { useDebouncedFilter } from '@/hooks/useDebouncedFilter';
import { sortingStateToSortConfig } from '@/utils/sortingStateToSortConfig';

const rolesData = ['ADMIN', 'EDITOR'];
const PAGE_SIZE = 20;

export default function UsersTable() {
  const {
    urlCurrentPage,
    updateUrlPage,
    urlSorting,
    updateUrlSorting,
    updateUrlFilter,
    getUrlFilterValue,
  } = useUrlTable({ type: TableType.USERS });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const { lang } = useContext(LangContext);
  const [openDeletingModal, setOpenDeletingModal] = useState(false);
  const [rowUserName, setRowUserName] = useState<string>('');
  const { organization_id: organizationId } = useParams();

  const emailFilter = getUrlFilterValue('email');
  const filters = useMemo(() => (emailFilter ? { email: emailFilter } : undefined), [emailFilter]);
  const sortConfig = useMemo(() => sortingStateToSortConfig(urlSorting), [urlSorting]);

  const { data: { items: organizationMembers, pagination } = {} } = useGetOrganizationMembers(
    organizationId,
    {
      page: urlCurrentPage,
      limit: PAGE_SIZE,
    },
    filters,
    sortConfig,
  );

  // Debounced filter for email search
  const { inputValue: emailInputValue, setInputValue: setEmailInputValue } = useDebouncedFilter({
    filterKey: 'email',
    currentValue: emailFilter,
    onFilterChange: updateUrlFilter,
  });

  const { columns, modals } = UserColumns(
    lang,
    rolesData,
    setOpenDeletingModal,
    setRowUserName,
    organizationId!,
  );

  const totalCount = pagination?.totalItems || 0;

  const table = useReactTable({
    data: organizationMembers ?? [],
    columns,
    pageCount: getPageCount(totalCount, PAGE_SIZE),
    onSortingChange: updateUrlSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    state: {
      sorting: urlSorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: urlCurrentPage - 1,
        pageSize: PAGE_SIZE,
      },
    },
  });

  // Sync URL filter values with table columnFilters
  useEffect(() => {
    setColumnFilters(emailFilter ? [{ id: 'email', value: emailFilter }] : []);
  }, [getUrlFilterValue]);

  return (
    <div className="w-full">
      {/* delete user modal */}
      <AlertDialog open={openDeletingModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {rowUserName}?</AlertDialogTitle>
            <AlertDialogDescription className="text-dark-text">
              If they have unshared workspaces, we&apos;ll add them to your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-5">
            <AlertDialogCancel onClick={() => setOpenDeletingModal(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => setOpenDeletingModal(false)}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove from organization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-wrap items-center gap-4 pt-4 pb-4">
        {/* invite members modal */}
        <InviteToOrganizationModal roles={rolesData} />

        {/* email filter and cols filtering */}
        <div className="flex gap-2 max1050:w-full max1050:flex-row-reverse md700:flex-col">
          <Input
            placeholder={lang.get('msg.filterEmails')}
            value={emailInputValue}
            onChange={(event) => {
              updateUrlPage(1);
              setEmailInputValue(event.target.value);
            }}
            className="w-64 md700:w-full"
          />
        </div>
        {/* filters end */}
      </div>
      <div className="border rounded-md">
        <Table className="text-dark-text">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {lang.get('msg.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        table={table}
        currentPage={urlCurrentPage}
        onPageChange={updateUrlPage}
      />
      {modals}
    </div>
  );
}
